import { execFile } from 'node:child_process'
import { promisify } from 'node:util'
import { readFile } from 'node:fs/promises'
import { getPty } from './pty-loader.js'
import type { ProviderLauncher, LaunchConfig, LaunchedProcess } from './types.js'
import { SessionRecorder } from '../session/recorder.js'
import { resolveBinaryOrFallback } from './resolve-binary.js'
import { isNpxPackageAvailable } from './npx-check.js'

const execFileAsync = promisify(execFile)

const NPX_PACKAGE = 'cline'

/**
 * Launcher for Cline CLI (`npm install -g cline`, cline.bot).
 *
 * Interactive TUI requires `-i/--tui`. Do not pass a positional prompt — that
 * starts one-shot act mode. Interview rules go in `-s/--system` so we never
 * write CLAUDE.md into the candidate workspace.
 */
export class ClineLauncher implements ProviderLauncher {
  readonly name = 'cline'
  readonly displayName = 'Cline'
  readonly pricingHint = '🆓 Gratuit (open-source, clé API personnelle requise)'

  async isInstalled(): Promise<boolean> {
    try {
      await execFileAsync('cline', ['--version'])
      return true
    } catch {
      return isNpxPackageAvailable(NPX_PACKAGE)
    }
  }

  async getVersion(): Promise<string | null> {
    try {
      const { stdout } = await execFileAsync('cline', ['--version'])
      return stdout.trim()
    } catch {
      return null
    }
  }

  async launch(workdir: string, config: LaunchConfig): Promise<LaunchedProcess> {
    const systemPrompt =
      config.systemPromptContent ?? (await readFile(config.systemPromptPath, 'utf-8'))

    const args: string[] = ['-i', '--cwd', workdir, '-s', systemPrompt]

    if (config.model) {
      args.push('-m', config.model)
    }

    if (config.permissionMode === 'plan') {
      args.push('-p')
    }

    if (config.permissionMode === 'bypassPermissions' || config.permissionMode === 'acceptEdits') {
      args.push('--auto-approve', 'true')
    } else {
      args.push('--auto-approve', 'false')
    }

    const recorder = new SessionRecorder()

    const binaryName = await this.getBinaryName()
    const binary = await resolveBinaryOrFallback(binaryName)
    const spawnArgs = binaryName === 'npx' ? [NPX_PACKAGE, ...args] : args

    const ptyProc = getPty().spawn(binary, spawnArgs, {
      name: 'xterm-256color',
      cwd: workdir,
      cols: process.stdout.columns ?? 80,
      rows: process.stdout.rows ?? 24,
      env: { ...process.env },
    })

    const cleanup = SessionRecorder.bridgePTY(ptyProc, recorder)

    // Do NOT auto-inject a first prompt. The candidate must drive the session.

    return {
      wait: () =>
        new Promise((resolve) => {
          ptyProc.onExit(({ exitCode, signal }) => {
            cleanup()
            resolve({ exitCode, signal })
          })
        }),
      kill: async () => {
        cleanup()
        ptyProc.kill()
      },
      recorder,
    }
  }

  private async getBinaryName(): Promise<string> {
    try {
      await execFileAsync('cline', ['--version'])
      return 'cline'
    } catch {
      return 'npx'
    }
  }
}
